import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { HeaderMenuLabels, HeaderMenuSpecialItems } from "../components/header-menu/types";

export interface HeaderMenuContextValue {
  expandedItemId: string | null;
  toggleExpandedItemId: (itemId: string) => void;
  setExpandedItemId: Dispatch<SetStateAction<string | null>>;
  expandedTabId: string | null;
  toggleExpandedTabId: (itemId: string) => void;
  setExpandedTabId: Dispatch<SetStateAction<string | null>>;
  expandedEditorialItem: Queries.HeaderMenuItemDataFragment | null;
  expandedTab: Queries.HeaderMenuItemTabDataFragment | null;
  headerMenu: Queries.HeaderMenuDataFragment;
  pageContentfulId: string | null;
  internalPagePath: string | null;
  isMenuOpen: boolean;
  setMenuOpen: (isOpen: boolean) => void;
  isSpecialItemOpen: boolean;
  labels: HeaderMenuLabels;
  allMenuItems: Queries.HeaderMenuItemDataFragment[];
  isItemPanelExpanded: boolean;
  setItemPanelExpanded: Dispatch<SetStateAction<boolean>>;
}

export const HeaderMenuContext = createContext<HeaderMenuContextValue | null>(null);

export interface HeaderMenuProviderProps {
  headerMenu: Queries.HeaderMenuDataFragment;
  pageContentfulId: string | null;
  internalPagePath: string | null;
  labels: HeaderMenuLabels;
}

export function HeaderMenuProvider({
  children,
  labels,
  headerMenu,
  pageContentfulId,
  internalPagePath,
}: PropsWithChildren<HeaderMenuProviderProps>) {
  const { primaryItems, secondaryItems } = headerMenu;
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [expandedTabId, setExpandedTabId] = useState<string | null>(null);
  // used for opening mobile version of the menu
  const [isMenuOpen, _setMenuOpen] = useState(false);
  const setMenuOpen = useCallback((isOpen: boolean) => {
    // reset tab and item on close
    if (!isOpen) {
      setExpandedItemId(null);
      setExpandedTabId(null);
    }
    _setMenuOpen(isOpen);
  }, []);

  // used for opening a menu item in desktop version
  const [isItemPanelExpanded, setItemPanelExpanded] = useState(false);

  const allMenuItems = useMemo(() => {
    return [...Array.from(primaryItems ?? []), ...Array.from(secondaryItems ?? [])].filter(
      (item) => item?.__typename === "ContentfulHeaderMenuItem"
    ) as Queries.HeaderMenuItemDataFragment[];
  }, [primaryItems, secondaryItems]);

  const expandedEditorialItem = useMemo(() => {
    if (expandedItemId) {
      if ((Object.values(HeaderMenuSpecialItems) as string[]).includes(expandedItemId)) {
        return null;
      } else {
        const item = allMenuItems.find((item) => item.id === expandedItemId) ?? null;
        return item ?? null;
      }
    }
    return null;
  }, [expandedItemId, allMenuItems]);

  // on change expanded item, check if its first tab can be opened
  useEffect(() => {
    setExpandedTabId(expandedEditorialItem?.tabs?.[0]?.id ?? null);
  }, [expandedEditorialItem]);

  const expandedTab = useMemo(() => {
    return expandedEditorialItem?.tabs?.find((tab) => tab?.id === expandedTabId) ?? null;
  }, [expandedEditorialItem, expandedTabId]);

  const toggleExpandedItemId = useCallback((itemId: string) => {
    setExpandedItemId((id) => {
      if (id === itemId) {
        // close panel while keeping reference to selected item
        setItemPanelExpanded((isExpanded) => !isExpanded);
        return id;
      } else {
        setItemPanelExpanded(true);
        return itemId;
      }
    });
  }, []);

  const toggleExpandedTabId = useCallback((tabId: string) => {
    setExpandedTabId((id) => (id === tabId ? null : tabId));
  }, []);

  const isSpecialItemOpen =
    !!expandedItemId &&
    (Object.values(HeaderMenuSpecialItems) as string[]).includes(expandedItemId);

  const value = useMemo<HeaderMenuContextValue>(
    () => ({
      expandedItemId,
      toggleExpandedItemId,
      setExpandedItemId,
      expandedTabId,
      toggleExpandedTabId,
      setExpandedTabId,
      expandedEditorialItem,
      expandedTab,
      headerMenu,
      isMenuOpen,
      setMenuOpen,
      isSpecialItemOpen,
      labels,
      pageContentfulId,
      internalPagePath,
      allMenuItems,
      isItemPanelExpanded,
      setItemPanelExpanded,
    }),
    [
      expandedItemId,
      toggleExpandedItemId,
      expandedTabId,
      toggleExpandedTabId,
      expandedEditorialItem,
      expandedTab,
      headerMenu,
      isMenuOpen,
      isSpecialItemOpen,
      labels,
      pageContentfulId,
      internalPagePath,
      allMenuItems,
      isItemPanelExpanded,
      setItemPanelExpanded,
      setMenuOpen,
    ]
  );

  return <HeaderMenuContext.Provider value={value}>{children}</HeaderMenuContext.Provider>;
}
